import { setNonce } from "get-nonce";

/** Placeholder nginx replaces with $request_id; ignore in Vite local (no CSP). */
const PLACEHOLDER = "CSP_NONCE";

export function readCspNonce(): string | undefined {
    if (typeof document === "undefined") return undefined;
    const value = document.querySelector('meta[property="csp-nonce"]')?.getAttribute("content")?.trim();
    if (!value || value === PLACEHOLDER) return undefined;
    return value;
}

/**
 * Force every dynamically created <style> tag to carry the CSP nonce.
 * Catches Emotion (incl. its default cache), react-style-singleton, PDF.js
 * font rules, and any other library that forgets to set nonce.
 * Must run before those libraries insert styles (import this first in main).
 */
function installStyleNoncePatch(nonce: string) {
    const applyNonce = (el: HTMLElement) => {
        el.setAttribute("nonce", nonce);
        // IDL property — some browsers ignore setAttribute alone for CSP checks
        (el as HTMLStyleElement).nonce = nonce;
    };

    const origCreateElement = Document.prototype.createElement;
    Document.prototype.createElement = function createElement(
        this: Document,
        tagName: string,
        options?: ElementCreationOptions
    ) {
        const el = origCreateElement.call(this, tagName, options);
        if (String(tagName).toLowerCase() === "style") {
            applyNonce(el);
        }
        return el;
    } as typeof Document.prototype.createElement;

    const origCreateElementNS = Document.prototype.createElementNS;
    Document.prototype.createElementNS = function createElementNS(
        this: Document,
        namespaceURI: string | null,
        qualifiedName: string,
        options?: string | ElementCreationOptions
    ) {
        const el = origCreateElementNS.call(this, namespaceURI, qualifiedName, options as ElementCreationOptions);
        if (String(qualifiedName).toLowerCase() === "style") {
            applyNonce(el as HTMLElement);
        }
        return el;
    } as typeof Document.prototype.createElementNS;

    // If a <style> is inserted without going through createElement, stamp it.
    const observer = new MutationObserver((records) => {
        for (const record of records) {
            record.addedNodes.forEach((node) => {
                if (node instanceof HTMLStyleElement && node.nonce !== nonce) {
                    applyNonce(node);
                }
            });
        }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
}

const cspNonce = readCspNonce();

if (cspNonce) {
    installStyleNoncePatch(cspNonce);
    setNonce(cspNonce);
    (globalThis as typeof globalThis & { __webpack_nonce__?: string }).__webpack_nonce__ = cspNonce;
}

export { cspNonce };
